import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Plus } from "lucide-react";
import { adminApi, formatError } from "@/lib/adminApi";
import { AdminLogin } from "@/components/admin/AdminLogin";
import { PostCard } from "@/components/admin/PostCard";
import { AppointmentsPanel } from "@/components/admin/AppointmentsPanel";

const TABS = [
  { id: "posts", label: "Instagram posts" },
  { id: "appointments", label: "Appointments" },
];

const Dashboard = ({ user, onLogout }) => {
  const [posts, setPosts] = useState([]);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [p, s] = await Promise.all([adminApi.posts(), adminApi.status()]);
      setPosts(p);
      setStatus(s);
    } catch (err) {
      toast.error(formatError(err));
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const generating = posts.some((p) => p.status === "generating");
  useEffect(() => {
    if (!generating) return undefined;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [generating, load]);

  const onChange = (updated) => {
    if (updated?.status === "deleted") setPosts((ps) => ps.filter((p) => p.id !== updated.id));
    else if (updated?.id) setPosts((ps) => ps.map((p) => (p.id === updated.id ? updated : p)));
  };

  const generate = async () => {
    setBusy(true);
    try {
      const p = await adminApi.generate();
      setPosts((ps) => [p, ...ps]);
      toast.success("Drafting a new post — about a minute");
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="mx-auto max-w-5xl px-6 pb-24 pt-8">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#c8a97e]">Instagram · @jaisunhouse.irel</p>
          <h1 className="mt-3 font-display text-5xl sm:text-6xl">Daily posts</h1>
          <p className="mt-3 max-w-xl text-sm text-[#c9c6c0]">A new draft is created each day. Only approved posts are published, at 9:00 Irish time on their date. Unapproved drafts are never posted.</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="admin-generate-btn" disabled={busy} onClick={generate} className="flex items-center gap-2 rounded-full bg-[#c8a97e] px-5 py-3 text-[12px] font-medium uppercase tracking-[0.14em] text-[#0a0b0d] disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} New draft
          </button>
          <button data-testid="admin-logout-btn" aria-label="Sign out" onClick={onLogout} className="flex h-11 w-11 items-center justify-center rounded-full border border-white/20"><LogOut className="h-4 w-4" /></button>
        </div>
      </div>
      <div data-testid="admin-connection-status" className={`mt-8 border px-4 py-3 text-sm ${status?.publishing_connected ? "border-emerald-400/40 text-emerald-300" : "border-[#c8a97e]/40 text-[#e4d3b8]"}`}>
        {status?.publishing_connected ? "Instagram publishing is connected." : "Instagram publishing is not connected yet. You can draft and approve posts now; they will publish once the access token is added."}
      </div>
      <div className="mt-10 space-y-6">
        {posts.length === 0 && <p data-testid="admin-empty" className="text-sm text-[#b4b2ad]">No posts yet. Click “New draft” to create the first one.</p>}
        {posts.map((p) => <PostCard key={p.id} post={p} connected={!!status?.publishing_connected} onChange={onChange} />)}
      </div>
      <p className="mt-10 font-mono text-[11px] uppercase tracking-[0.2em] text-[#b4b2ad]">Signed in as {user.email}</p>
    </div>
  );
};

export default function AdminPage() {
  const [user, setUser] = useState(null);
  const [tab, setTab] = useState("posts");
  useEffect(() => {
    adminApi.me().then(setUser).catch(() => setUser(false));
  }, []);

  if (user === null) return <div className="flex min-h-[70vh] items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[#c8a97e]" /></div>;
  if (!user) return <AdminLogin onDone={setUser} />;
  const logout = async () => { await adminApi.logout(); setUser(false); };
  return (
    <>
      <div className="mx-auto flex max-w-5xl gap-2 px-6 pt-28">
        {TABS.map((t) => (
          <button key={t.id} data-testid={`admin-tab-${t.id}`} onClick={() => setTab(t.id)} className={`rounded-full px-5 py-2 text-[12px] uppercase tracking-[0.14em] transition-colors ${tab === t.id ? "bg-[#f5f3ef] text-[#0a0b0d]" : "border border-white/20 text-[#d6d3cd]"}`}>{t.label}</button>
        ))}
      </div>
      {tab === "posts" ? <Dashboard user={user} onLogout={logout} /> : <AppointmentsPanel />}
    </>
  );
}
